/**
 * Object Field Type Detection & Rendering
 *
 * Classifies each field value in an object node and provides
 * type-appropriate HTML rendering. Used by ObjectRenderer.
 */

import { marked } from 'marked';

// Configure marked for synchronous rendering with GFM
marked.use({ async: false, gfm: true, breaks: true });

// =============================================================================
// Field Types
// =============================================================================

export type FieldType =
	| 'text'
	| 'long-text'
	| 'markdown'
	| 'image-url'
	| 'image-data'
	| 'url'
	| 'json'
	| 'number'
	| 'boolean';

export interface ClassifiedField {
	key: string;
	value: unknown;
	type: FieldType;
	/** String representation for display */
	displayValue: string;
}

// =============================================================================
// Detection Patterns
// =============================================================================

/** Image URL file extension patterns */
const IMAGE_URL_PATTERN =
	/\.(png|jpg|jpeg|gif|webp|svg|bmp|ico|avif)(\?|#|$)/i;

/** Data URI image pattern */
const IMAGE_DATA_PATTERN = /^data:image\//i;

/** Rool media CDN pattern */
const ROOL_MEDIA_PATTERN = /rool\.dev\/media/i;

/** URL pattern (http/https) */
const URL_PATTERN = /^https?:\/\//i;

/** Markdown detection patterns (same as infer-render-type) */
const MARKDOWN_PATTERNS: RegExp[] = [
	/^#{1,6}\s/m,          // headings
	/^\s*[-*+]\s/m,        // unordered lists
	/^\s*\d+\.\s/m,        // ordered lists
	/\*\*[^*]+\*\*/,       // bold
	/```[\s\S]*?```/,      // code blocks
	/^\s*>/m,              // blockquotes
	/\[.+\]\(.+\)/,       // links
	/!\[.*\]\(.+\)/,      // images
];

/** Minimum text length to consider markdown detection */
const MARKDOWN_MIN_LENGTH = 30;

/** Minimum markdown patterns required */
const MARKDOWN_MIN_PATTERNS = 2;

/** Threshold for "long text" vs "text" (chars) */
const LONG_TEXT_THRESHOLD = 80;

/** Max image display height in px */
const IMAGE_MAX_HEIGHT = 200;

/** Max JSON display length before truncating */
const JSON_MAX_LENGTH = 500;

// =============================================================================
// Classification
// =============================================================================

/**
 * Detect if a string contains markdown formatting.
 * Requires length >= MARKDOWN_MIN_LENGTH and >= MARKDOWN_MIN_PATTERNS patterns.
 */
export function isMarkdown(text: string): boolean {
	if (text.length < MARKDOWN_MIN_LENGTH) return false;
	let matches = 0;
	for (const pattern of MARKDOWN_PATTERNS) {
		if (pattern.test(text)) {
			matches++;
			if (matches >= MARKDOWN_MIN_PATTERNS) return true;
		}
	}
	return false;
}

/** Detect if a string is an image URL or data URI */
export function isImageValue(value: string): boolean {
	return IMAGE_DATA_PATTERN.test(value) ||
		IMAGE_URL_PATTERN.test(value) ||
		ROOL_MEDIA_PATTERN.test(value);
}

/** Detect if a string is a URL */
export function isUrl(value: string): boolean {
	return URL_PATTERN.test(value);
}

/**
 * Classify a single field value into its rendering type.
 */
export function classifyField(key: string, value: unknown): ClassifiedField {
	// Null/undefined → text
	if (value === null || value === undefined) {
		return { key, value, type: 'text', displayValue: String(value) };
	}

	// Boolean
	if (typeof value === 'boolean') {
		return { key, value, type: 'boolean', displayValue: String(value) };
	}

	// Number
	if (typeof value === 'number') {
		return { key, value, type: 'number', displayValue: String(value) };
	}

	// Object or Array → JSON
	if (typeof value === 'object') {
		const json = JSON.stringify(value, null, 2);
		return { key, value, type: 'json', displayValue: json };
	}

	// String classification
	if (typeof value === 'string') {
		// Image data URI
		if (IMAGE_DATA_PATTERN.test(value)) {
			return { key, value, type: 'image-data', displayValue: value };
		}

		// Image URL (file extension or CDN match)
		if (IMAGE_URL_PATTERN.test(value) || ROOL_MEDIA_PATTERN.test(value)) {
			return { key, value, type: 'image-url', displayValue: value };
		}

		// Markdown (must check before URL since markdown can contain URLs)
		if (isMarkdown(value)) {
			return { key, value, type: 'markdown', displayValue: value };
		}

		// Non-image URL
		if (URL_PATTERN.test(value)) {
			return { key, value, type: 'url', displayValue: value };
		}

		// Long text vs short text
		if (value.length > LONG_TEXT_THRESHOLD) {
			return { key, value, type: 'long-text', displayValue: value };
		}

		return { key, value, type: 'text', displayValue: value };
	}

	// Fallback
	return { key, value, type: 'text', displayValue: String(value) };
}

// =============================================================================
// HTML Rendering
// =============================================================================

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * Render a classified field value to HTML.
 */
export function renderFieldValue(field: ClassifiedField): string {
	switch (field.type) {
		case 'image-url':
		case 'image-data':
			return `<div class="object-field-image"><img src="${escapeHtml(field.displayValue)}" alt="${escapeHtml(field.key)}" style="max-height: ${IMAGE_MAX_HEIGHT}px; max-width: 100%; border-radius: 8px; object-fit: contain;" loading="lazy" /></div>`;

		case 'markdown':
			return `<div class="object-field-markdown">${marked.parse(field.displayValue) as string}</div>`;

		case 'json': {
			const truncated = field.displayValue.length > JSON_MAX_LENGTH
				? field.displayValue.slice(0, JSON_MAX_LENGTH - 3) + '...'
				: field.displayValue;
			return `<pre class="object-field-json"><code>${escapeHtml(truncated)}</code></pre>`;
		}

		case 'url':
			return `<a class="object-field-url" href="${escapeHtml(field.displayValue)}" target="_blank" rel="noopener">${escapeHtml(field.displayValue)}</a>`;

		case 'boolean':
			return `<span class="object-field-bool object-field-bool--${field.displayValue}">${field.displayValue}</span>`;

		case 'number':
			return `<span class="object-field-number">${escapeHtml(field.displayValue)}</span>`;

		case 'long-text':
			return `<div class="object-field-longtext">${escapeHtml(field.displayValue)}</div>`;

		case 'text':
		default:
			return `<span class="object-field-text">${escapeHtml(field.displayValue)}</span>`;
	}
}

/**
 * Check if a field type is editable (text-based, not image/blob).
 */
export function isEditableFieldType(type: FieldType): boolean {
	switch (type) {
		case 'text':
		case 'long-text':
		case 'markdown':
		case 'url':
		case 'number':
		case 'json':
			return true;
		case 'image-url':
		case 'image-data':
		case 'boolean':
			return false;
		default:
			return false;
	}
}
